import { log } from '../log'

const PROXY = import.meta.env.VITE_PROXY

if (!PROXY) {
  throw 'PROXY env not configured!'
}

const WS = '_ws'

export function setCurrentWorksheet(worksheetId: string) {
  localStorage.setItem(WS, worksheetId);
}

export function redirectToLogin(): void {
  setCurrentWorksheet(null);

  const url = `${PROXY}/security/login?application_uri=${window.location.href}`
  window.location.replace(url)
}

export function redirectToLogout(logoutUrl?: string): void {
  setCurrentWorksheet(null);
  const url = `${PROXY}/security/logout?application_uri=${logoutUrl || window.location.href}`
  window.location.replace(url)
}

export async function isAuthenticated(redirect = false): Promise<boolean> {
  try {
    const r = await fetch(PROXY + '/security/', {
      credentials: 'include',
    })
    const c = (await responseContent(r)) as string
    const auth = c?.length > 0
    if (!auth && redirect) {
      redirectToLogin()
    }
    return auth
  } catch (_) {
    if (redirect) {
      redirectToLogin()
    }
    return false
  }
}

export async function getToken(): Promise<string> {
  try {
    const r = await fetch(PROXY + '/security/gettoken', {
      credentials: 'include',
    })
    const token = await r.text()
    return token
  } catch (_) {
    return undefined
  }
}

export function responseContent(r: Response): Promise<unknown> {
  const contentType = r?.headers?.get('content-type')
  if (contentType && contentType.indexOf('json') !== -1) {
    return r.json()
  }
  if (contentType && contentType.indexOf('text') !== -1) {
    return r.text()
  }
  const contentLength = r?.headers?.get('content-length')
  if (!contentLength?.length) {
    return Promise.resolve('')
  }
  return r.blob()
}

type ProxyFetchInit = RequestInit & {
  method: string
  url?: string
  redirectOnUnauthorized?: boolean
  form?: boolean
  disableBodyEdit?: boolean
  body?: Record<string, unknown> & {
    service?: string
    action?: string
  }
  responseContent?: (r: Response) => Promise<unknown>
}
const TIMEOUT = 1000 * 30

export async function proxyFetch<T = any>({
  method,
  url,
  body,
  redirectOnUnauthorized,
  form,
  disableBodyEdit,
  ...init
}: ProxyFetchInit): Promise<T> {
  const isPost = method.toLowerCase() === 'post'
  const isService = isPost && !!body?.service?.length
  const path = isService ? '/security/execute' : isPost ? '/security/executepost' : '/security/executeget'
  const bodyString = body
    ? form
      ? body
      : disableBodyEdit
      ? JSON.stringify(body)
      : JSON.stringify(body).replace(/\\n/g, ' ')
    : undefined

  let r: Response
  const endpoint = PROXY + path + (url || '')
  try {
    r = await fetch(endpoint, {
      ...init,
      ...{
        method,
        //@ts-ignore ts is ignorant
        signal: AbortSignal.timeout(TIMEOUT),
        body: bodyString ? bodyString : undefined,
        credentials: 'include',
      },
    })
  } catch (e) {
    const error = (e || 'Unknown error').toString()
    log.error({ endpoint, error })
    return Promise.reject(error)
  }

  if (r.status === 302 && redirectOnUnauthorized) {
    redirectToLogin()
  }

  const fn = init.responseContent || responseContent
  return (await fn(r)) as T
}
